import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import type { StatusBadgeColor } from "@/components/Atoms/StatusBadge/StatusBadge.types";
import { StatusBadge } from "@/components/Atoms";
import { toAmount } from "@/lib/billing/constants";
import { formatPersianNumber } from "@/lib/format/persian";
import type { BillingShopRowProps } from "./BillingShopRow.types";
import * as styles from "./BillingShopRow.styles";

export type { BillingShopRowProps } from "./BillingShopRow.types";

function resolveStatus(isRestricted: boolean, debt: number): { labelKey: string; color: StatusBadgeColor } {
  if (isRestricted) return { labelKey: "shops.restricted", color: "error" };
  if (debt > 0) return { labelKey: "shops.hasDebt", color: "warning" };
  return { labelKey: "shops.clear", color: "success" };
}

export const BillingShopRow = memo(function BillingShopRow({
  shop,
  debtLimit,
  canPayDebt = true,
  isPayPending = false,
  isPaying = false,
  onPayDebt,
}: BillingShopRowProps) {
  const { t } = useTranslation("billing");

  const debt = toAmount(shop.outstanding_debt);
  const isRestricted = shop.is_restricted;
  const status = resolveStatus(isRestricted, debt);
  const initial = shop.store_name.trim().charAt(0);
  const showPayButton = canPayDebt && debt > 0;
  const debtPercent = debtLimit > 0 ? Math.min(100, Math.round((debt / debtLimit) * 100)) : 0;

  const subText = debt > 0
    ? t("shops.debt", { amount: formatPersianNumber(debt) })
    : t("shops.noDebt");
  const commissionText = t("shops.commissionTotal", {
    amount: formatPersianNumber(toAmount(shop.total_commission)),
  });

  const badges = (
    <>
      <StatusBadge color={status.color} size="sm" label={t(status.labelKey)} />
      {shop.online_payment_enabled ? null : (
        <StatusBadge color="warning" size="sm" label={t("shops.noIban")} />
      )}
    </>
  );

  const payButton = (mobile: boolean) =>
    showPayButton ? (
      <Button
        variant={isRestricted ? "contained" : "outlined"}
        color="primary"
        size={mobile ? "small" : "medium"}
        fullWidth={false}
        disabled={isPayPending}
        onClick={() => onPayDebt?.(shop.store_id)}
        sx={mobile ? styles.payButtonMobile : styles.payButtonDesktop}
      >
        {isPaying ? <CircularProgress size={mobile ? 16 : 18} color="inherit" /> : t("shops.payDebt")}
      </Button>
    ) : null;

  const debtBar =
    debt > 0 ? (
      <Box sx={styles.progressTrack}>
        <Box sx={styles.progressFill(debtPercent, isRestricted)} />
      </Box>
    ) : null;

  return (
    <>
      <Box sx={styles.desktopRoot(isRestricted)}>
        <Box sx={styles.avatar(isRestricted)}>{initial}</Box>
        <Box sx={styles.desktopInfo}>
          <Box sx={styles.desktopNameRow}>
            <Typography sx={styles.desktopName}>{shop.store_name}</Typography>
            {badges}
          </Box>
          <Typography sx={styles.subText(isRestricted)}>{subText}</Typography>
          {debtBar}
        </Box>
        <Box sx={styles.desktopPriceBlock}>
          <Typography sx={styles.priceUnit}>{commissionText}</Typography>
        </Box>
        {payButton(false)}
      </Box>

      <Box sx={styles.mobileRoot(isRestricted)}>
        <Box sx={styles.mobileHeader}>
          <Box sx={styles.avatar(isRestricted)}>{initial}</Box>
          <Box sx={styles.mobileNameBlock}>
            <Typography sx={styles.mobileName}>{shop.store_name}</Typography>
            <Typography sx={styles.subText(isRestricted)}>{subText}</Typography>
          </Box>
          <StatusBadge color={status.color} size="sm" label={t(status.labelKey)} />
        </Box>
        {debtBar}
        <Box sx={styles.mobileFooter}>
          <Typography sx={styles.priceUnit}>
            {shop.online_payment_enabled ? commissionText : t("shops.noIban")}
          </Typography>
          {payButton(true)}
        </Box>
      </Box>
    </>
  );
});
